/**
 * TanStack Query Client Configuration
 * 
 * Configures React Query for server state management with:
 * - Proper error handling
 * - Auth error handling (401 -> logout)
 * - Retry policies
 * - Cache invalidation strategies
 */

import { QueryClient } from '@tanstack/react-query';
import { ApiClientError } from '../api/client';
import { firebaseAuthService } from '../../../frontend/services/firebaseAuth.service';

// Create query client with custom configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: how long data is considered fresh
      staleTime: 5 * 60 * 1000, // 5 minutes
      
      // Cache time: how long unused data stays in cache
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      
      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry on auth errors or client errors
        if (error instanceof ApiClientError) {
          if (error.isAuthError || (error.status >= 400 && error.status < 500)) {
            return false;
          }
        }
        
        // Retry up to 3 times for server errors
        return failureCount < 3;
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus (but not too aggressively)
      refetchOnWindowFocus: false,
      
      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    
    mutations: {
      // Global error handling for mutations
      onError: (error) => {
        console.error('Mutation error:', error);
        
        // Handle auth errors globally
        if (error instanceof ApiClientError && error.isAuthError) {
          console.log('Authentication error, signing out user');
          firebaseAuthService.signOut();
        }
      },
    },
  },
});

// Global error handler for queries
queryClient.getQueryCache().config.onError = (error) => {
  console.error('Query error:', error);
  
  // Handle auth errors globally
  if (error instanceof ApiClientError && error.isAuthError) {
    console.log('Authentication error, signing out user');
    firebaseAuthService.signOut();
  }
};

export default queryClient;
 